import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { THEME, type Theme } from '../lib/theme';

type SystemTheme = Exclude<Theme, 'system'>;

type ThemeProviderProps = {
    children: ReactNode;
    attribute?: 'class' | 'data-theme';
    defaultTheme?: Theme;
    disableTransitionOnChange?: boolean;
};

type ThemeContextType = {
    theme: Theme;
    setTheme: (theme: Theme) => void;
    systemTheme: SystemTheme;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'theme';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';
const DISABLE_TRANSITION_STYLE =
    '*, *::before, *::after { -webkit-transition: none !important; -moz-transition: none !important; -o-transition: none !important; -ms-transition: none !important; transition: none !important; }';

function getSystemTheme(): SystemTheme {
    return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light';
}

function getStoredTheme(): Theme | null {
    return (localStorage.getItem(STORAGE_KEY) as Theme) || null;
}

function setStoredTheme(theme: Theme): void {
    localStorage.setItem(STORAGE_KEY, theme);
}

export function ThemeProvider({
    children,
    attribute = 'class',
    defaultTheme = THEME.SYSTEM,
    disableTransitionOnChange = false,
}: ThemeProviderProps) {
    const [theme, setThemeState] = useState<Theme>(() => {
        const stored = getStoredTheme();

        return stored || defaultTheme;
    });
    const [systemTheme, setSystemTheme] = useState<SystemTheme>(() => {
        return getSystemTheme();
    });

    useEffect(() => {
        const mediaQuery = window.matchMedia(MEDIA_QUERY);
        const handleChange = (e: MediaQueryListEvent) => {
            setSystemTheme(e.matches ? 'dark' : 'light');
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    useEffect(() => {
        const root = document.documentElement;
        const resolvedTheme = theme === THEME.SYSTEM ? systemTheme : theme;

        if (disableTransitionOnChange) {
            const css = document.createElement('style');
            css.setAttribute('data-theme-transition', '');
            css.textContent = DISABLE_TRANSITION_STYLE;
            document.head.appendChild(css);

            void window.getComputedStyle(root).opacity;

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    document.head.removeChild(css);
                });
            });
        }

        if (attribute === 'class') {
            root.classList.remove('light', 'dark');
            root.classList.add(resolvedTheme);
        } else {
            root.setAttribute(attribute, resolvedTheme);
        }
    }, [theme, systemTheme, attribute, disableTransitionOnChange]);

    const setTheme = useCallback(
        (newTheme: Theme) => {
            setThemeState(newTheme);
            setStoredTheme(newTheme);
        },
        [setThemeState],
    );

    return (
        <ThemeContext.Provider value={{ theme, setTheme, systemTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);

    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }

    return context;
}
